// @flow
import React, {useEffect, useState} from "react";
import {useFormik} from "formik";
import {useObserver} from "mobx-react-lite";
import {
    Input,
    Form,
    Modal,
    Radio,
    Table,
    Checkbox,
    Tooltip,
    Popover,
    Col,
    Space,
    Select,
    Switch
} from "antd";
import {CloseCircleTwoTone, InfoCircleOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {
    RadioContainer,
    RadioList,
    BottomCheckbox,
    Text,
    CancelInput
} from "./textBoxFormModalStyles";
import * as yup from "yup";
import {selectReasons, productReturnSelectReasons} from "../../../constants/constants";

import {TBFMFormPropType, TBFMPropType} from "./TBFMPropTypes";
import TransferModal from "./TransferModal/TransferModal";
import {EligibleCourierSelect} from "../EligibleCourierSelect/EligibleCourierSelect";
const {Column} = Table;

export const TextBoxFormModal = ({
    deliveryId,
    isVisible,
    setVisible,
    okFunction,
    label,
    title,
    modalType,
    modalData,
    cancelFn,
    deliveryType = "delivery",
    deliveryData,
    allowMergeDefault = false,
    reasonOptions,
    allPoolCouriers,
    isCustomer = false
}: TBFMPropType) => {
    const {t} = useTranslation(["selectReasons", "textBoxFormModal"]);
    const [moreInfo, setMoreInfo] = useState(false);
    const [reasonText, setReasonText] = useState("");
    const [checked, setChecked] = useState([]);
    const [allowMerge, setAllowMerge] = useState(allowMergeDefault);
    const [resend, setResend] = useState(false);
    const [triggerInMinutes, setTriggerInMinutes] = useState(undefined);
    const [forceCourierId, setForceCourierId] = useState(undefined);
    const [shouldRefundFee, setShouldRefundFee] = useState(true);
    const [shouldSendCallback, setshouldSendCallback] = useState(true);

    const validationRules = modalType => {
        if (
            modalType === "cancel" ||
            modalType === "pass" ||
            modalType === "cancelOrder" ||
            modalType === "cancelOrderByNumber" ||
            modalType === "splitOrder"
        ) {
            if (!reasonText) {
                return yup.object().shape({
                    reason: yup
                        .string()
                        .required(t("validation.selectionRequired", {ns: "textBoxFormModal"}))
                        .min(5, t("validation.min5Letter", {ns: "textBoxFormModal"}))
                });
            } else if (reasonText.length <= 5) {
                return yup.object().shape({
                    reason: yup
                        .string()
                        .required(t("validation.reasonRequired", {ns: "textBoxFormModal"}))
                        .min(5, t("validation.min5Letter", {ns: "textBoxFormModal"}))
                });
            }
        }
    };

    const formik = useFormik({
        initialValues: {text: "", detailText: "", reason: reasonOptions?.defaultValue || ""},
        validationSchema: validationRules(modalType),
        onSubmit: async (values: TBFMFormPropType) => {
            if (modalType === "cancel") {
                okFunction({
                    type: modalData.type,
                    deliveryId: modalData.deliveryId,
                    orderId: modalData.orderId,
                    reason: reasonText ? {label: reasonText, value: "other"} : values.reason,
                    cancelKey: values.cancelKey,
                    shouldRefundFee,
                    shouldSendCallback
                });
            } else if (modalType === "splitOrder") {
                const checkedIds = checked.map(element => element.id);
                okFunction({
                    deliveryId: modalData.deliveryId,
                    allowMerge,
                    orderIds: checkedIds,
                    forceCourierId,
                    reason: reasonText || values.reason,
                    triggerInMinutes
                });
            } else if (modalType === "pass") {
                okFunction({
                    deliveryId: modalData.deliveryId,
                    orderId: modalData.orderId,
                    reason: reasonText || values.reason,
                    shouldRefundFee,
                    shouldSendCallback
                });
            } else if (modalType === "cancelOrder") {
                okFunction({
                    deliveryId: modalData.deliveryId,
                    orderId: modalData.orderId,
                    reason: reasonText || values.reason,
                    shouldRefundFee,
                    shouldSendCallback
                });
            } else if (modalType === "cancelOrderByNumber") {
                okFunction({
                    orderNumber: modalData.orderNumber,
                    reason: reasonText ? {label: reasonText, value: "other"} : values.reason,
                    cancelKey: values.cancelKey,
                    resend,
                    triggerInMinutes,
                    forceCourierId,
                    shouldRefundFee,
                    shouldSendCallback
                });
            } else {
                okFunction();
            }
        },
        enableReinitialize: false
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        resetForm,
        handleBlur
    } = formik;

    useEffect(() => {
        if (!isVisible) {
            resetForm();
            setMoreInfo(false);
            setReasonText("");
            setResend(false);
            setTriggerInMinutes(undefined);
            setShouldRefundFee(true);
            setshouldSendCallback(true);
        }
    }, [isVisible]);

    const handleEventChange = event => {
        if (event.target.value.value === "other") {
            setMoreInfo(true);
        } else {
            setReasonText("");
            setMoreInfo(false);
            setResend(false);
            setTriggerInMinutes(undefined);
        }
    };

    const deliveryTypeHandler = deliveryType => {
        return (deliveryType === "productReturn" ? productReturnSelectReasons : selectReasons).map(
            (reason, i) => {
                return (
                    <React.Fragment key={i}>
                        <RadioList key={reason.value} data-testid={`reason`} value={reason}>
                            {t(reason.translation_key, {ns: "selectReasons"})}
                        </RadioList>
                    </React.Fragment>
                );
            }
        );
    };

    const renderMoreInfo = () => {
        if (moreInfo) {
            return (
                <Input.TextArea
                    style={{marginTop: "10px"}}
                    name="reason"
                    value={reasonText}
                    onChange={e => setReasonText(e.target.value)}
                    data-testid={`${modalType}Modal_detailTextArea`}
                />
            );
        } else {
            return <React.Fragment />;
        }
    };

    const renderRadioGroup = () => (
        <RadioContainer>
            <Radio.Group
                name="reason"
                value={values.reason}
                onChange={e => {
                    handleChange(e);
                    handleEventChange(e);
                }}
                data-testid={`${modalType}Modal_reasons`}
            >
                {deliveryTypeHandler(deliveryType)}
            </Radio.Group>
        </RadioContainer>
    );

    const renderCancelOrderOptions = (marginTop = "10px") => {
        return (
            <Col style={{marginTop: marginTop}}>
                <Space direction="vertical" size={8}>
                    <Space size={8}>
                        <Switch
                            checked={shouldRefundFee}
                            onChange={value => setShouldRefundFee(value)}
                        />
                        <span>{t("shouldRefundFee", {ns: "selectReasons"})}</span>
                        <Popover
                            placement="top"
                            trigger={["hover", "click"]}
                            content={
                                "Aktif edildiğinde firmaya ücret iade edilecek ve kurye hakediş kazanamayacaktır."
                            }
                        >
                            <InfoCircleOutlined style={{color: "#8c8c8c"}} />
                        </Popover>
                    </Space>
                    <Space size={8}>
                        <Switch
                            checked={shouldSendCallback}
                            onChange={value => setshouldSendCallback(value)}
                        />
                        <span>{t("shouldSendCallback", {ns: "selectReasons"})}</span>
                        <Popover
                            placement="top"
                            trigger={["hover", "click"]}
                            content={
                                "Aktif edildiğinde müşterinin entegrasyonuna iptal bilgisi gönderilecektir."
                            }
                        >
                            <InfoCircleOutlined style={{color: "#8c8c8c"}} />
                        </Popover>
                    </Space>
                </Space>
            </Col>
        );
    };
    const renderCancelorderOptionsForCustomer = (marginTop = "10px") => {
        return (
            <Col style={{marginTop: marginTop}}>
                <Space direction="vertical" size={8}>
                    <Space size={8}>
                        <Switch
                            checked={shouldSendCallback}
                            onChange={value => setshouldSendCallback(value)}
                        />
                        <span>{t("shouldSendCallback", {ns: "selectReasons"})}</span>
                        <Popover
                            placement="top"
                            trigger={["hover", "click"]}
                            content={
                                "Aktif edildiğinde müşterinin entegrasyonuna iptal bilgisi gönderilecektir."
                            }
                        >
                            <InfoCircleOutlined style={{color: "#8c8c8c"}} />
                        </Popover>
                    </Space>
                </Space>
            </Col>
        );
    };
    const renderSelectReason = () => {
        let selectReasonsArr = [];

        selectReasonsArr.push(
            ...(deliveryType === "productReturn" ? productReturnSelectReasons : selectReasons)
        );

        return (
            <Select
                placeholder={"Bir neden seçiniz"}
                name="reason"
                data-testid="reasonSelect"
                value={values.reason}
                disabled={reasonOptions?.disabled}
                onChange={e => {
                    handleChange({target: {name: "reason", value: e}});
                    handleEventChange({target: {name: "reason", value: e}});
                }}
            >
                {selectReasonsArr.map((reason, i) => {
                    return (
                        <Select.Option key={reason.value} value={reason.value}>
                            {t(reason.translation_key, {ns: "selectReasons"})}
                        </Select.Option>
                    );
                })}
            </Select>
        );
    };

    const renderFormItem = () => {
        if (["cancel", "pass", "cancelOrder", "cancelOrderByNumber"].includes(modalType)) {
            return (
                <>
                    <Form.Item
                        colon={false}
                        label={label}
                        labelAlign="left"
                        validateStatus={errors.reason && touched.reason ? "error" : "success"}
                        help={errors.reason && touched.reason ? errors.reason : null}
                    >
                        {renderRadioGroup()}
                        {renderMoreInfo()}
                        {isCustomer ? renderCancelorderOptionsForCustomer(): renderCancelOrderOptions()}
                    </Form.Item>
                    {["cancelOrderByNumber"].includes(modalType) && (
                        <Col>
                            {resend ? (
                                <Col style={{marginTop: "10px"}}>
                                    <EligibleCourierSelect
                                        subCompanyId={
                                            deliveryData?.delivery?.orders.find(
                                                o => o?.orderNumber === modalData?.orderNumber
                                            )?.pickupInfoId
                                        }
                                        courierType={deliveryData?.delivery?.courierType}
                                        strict={true}
                                        excludedCouriers={[
                                            deliveryData?.delivery?.assignedCourier?._id?.toString()
                                        ]}
                                        initialCall={true}
                                        value={forceCourierId}
                                        onChange={courierId => setForceCourierId(courierId)}
                                    />
                                </Col>
                            ) : (
                                <></>
                            )}
                        </Col>
                    )}
                </>
            );
        } else if (modalType === "splitOrder") {
            //@TODO: split to new modal
            return (
                <Form.Item colon={false} label={label} labelAlign="left">
                    <Table
                        rowKey="_id"
                        dataSource={modalData.orderSteps}
                        pagination={false}
                        data-testid={`${modalType}Modal_customersTable`}
                        style={{border: "2px solid #e8e8e8", marginBottom: "10px"}}
                    >
                        <Column
                            dataIndex="stepInfo"
                            render={text => {
                                return text?.canceled ? (
                                    <Tooltip
                                        placement={"top"}
                                        title={t("alreadyCanceledTooltip", {
                                            ns: "textBoxFormModal"
                                        })}
                                        data-testid={`${modalType}Modal_customersTable_alreadytCanceled`}
                                    >
                                        <CloseCircleTwoTone twoToneColor="#f50" />
                                    </Tooltip>
                                ) : (
                                    <Checkbox
                                        data-testid={`${modalType}Modal_customersTable_checked`}
                                        checked={
                                            checked.filter(element => element.id === text._id)
                                                .length > 0
                                        }
                                        onChange={async e => {
                                            if (e.target.checked) {
                                                if (
                                                    checked.filter(
                                                        element => element.id === text._id
                                                    ).length === 0
                                                ) {
                                                    const obj = {
                                                        id: text._id,
                                                        value: e.target.checked
                                                    };
                                                    setChecked([...checked, obj]);
                                                }
                                            } else {
                                                setChecked(
                                                    checked.filter(
                                                        element => element.id !== text._id
                                                    )
                                                );
                                            }
                                        }}
                                        disabled={text?.canceled}
                                    />
                                );
                            }}
                        />
                        <Column
                            dataIndex="stepInfo"
                            title={
                                deliveryType === "productReturn"
                                    ? t("fields.returnPoint", {ns: "common"})
                                    : t("fields.customer", {ns: "common"})
                            }
                            data-testid={`${modalType}Modal_customersTable_${
                                deliveryType === "productReturn" ? "returnPoint" : "customer"
                            }`}
                            render={text => (
                                <Text cancel={text?.canceled}>{text.receiverName}</Text>
                            )}
                        />
                        <Column
                            dataIndex="stepInfo"
                            title={t("fields.orderNumber", {ns: "common"})}
                            data-testid={`${modalType}Modal_customersTable_orderNumber`}
                            render={text => <Text cancel={text?.canceled}>{text.orderNumber}</Text>}
                        />
                        <Column
                            dataIndex="stepInfo"
                            title={t("fields.address", {ns: "common"})}
                            data-testid={`${modalType}Modal_customersTable_address`}
                            render={text => (
                                <Text cancel={text?.canceled}>{text.receiverAddress}</Text>
                            )}
                        />
                    </Table>
                    <h4 style={{marginTop: "20px", marginBottom: "5px"}}>
                        {t("splitOrder.reason", {ns: "textBoxFormModal"})}
                    </h4>
                    <Form.Item
                        colon={false}
                        labelAlign="left"
                        validateStatus={errors.reason && touched.reason ? "error" : "success"}
                        help={errors.reason && touched.reason ? errors.reason : null}
                        style={{marginBottom: "10px"}}
                    >
                        {renderSelectReason()}
                        {renderMoreInfo()}
                    </Form.Item>
                    <Col style={{marginTop: "20px"}}>
                        <h4>{t("splitOrder.eligibleCourier", {ns: "textBoxFormModal"})}</h4>
                        <EligibleCourierSelect
                            subCompanyId={deliveryData?.delivery?.orders[0]?.pickupInfoId}
                            courierType={deliveryData?.delivery?.courierType}
                            excludedCouriers={[
                                deliveryData?.delivery?.assignedCourier?._id?.toString()
                            ]}
                            initialCall={true}
                            value={forceCourierId}
                            onChange={courierId => setForceCourierId(courierId)}
                            allowClear={true}
                            allPoolCouriers={allPoolCouriers}
                            //strict={true}
                            style={{marginBottom: "5px"}}
                        />

                        <h4 style={{marginTop: "20px"}}>
                            {t("splitOrder.delayEligible", {ns: "textBoxFormModal"})}
                        </h4>
                        <CancelInput
                            type="number"
                            name="triggerInMinutes"
                            placeholder={t("triggerInMinutes", {ns: "textBoxFormModal"})}
                            value={triggerInMinutes}
                            onChange={e => setTriggerInMinutes(e.target.value)}
                            data-testid={`${modalType}Modal_triggerInMinutes`}
                            style={{marginTop: "0px", marginBottom: "5px"}}
                        />

                        <BottomCheckbox
                            checked={allowMerge}
                            onChange={e => setAllowMerge(e.target.checked)}
                            data-testid={`${modalType}Modal_mergable`}
                            style={{marginLeft: "0px"}}
                        >
                            {t("allowMergeCheckbox", {ns: "textBoxFormModal"})}
                        </BottomCheckbox>
                    </Col>
                </Form.Item>
            );
        } else if (modalType === "inPrep") {
            return t("inPrep.modalInfo", {ns: "textBoxFormModal"});
        } else if (modalType === "unapproved") {
            return t("unapproved.modalInfo", {ns: "textBoxFormModal"});
        } else {
            return <React.Fragment />;
        }
    };

    if (modalType === "transfer") {
        return useObserver(() => (
            <TransferModal
                deliveryId={deliveryId}
                isVisible={isVisible}
                setVisible={setVisible}
                okFunction={okFunction} // bunu içeri taşı
                cancelFn={cancelFn}
            />
        ));
    }

    return useObserver(() => (
        <React.Fragment>
            <Modal
                visible={isVisible}
                centered
                title={title}
                okText={
                    modalType === "splitOrder"
                        ? t("createNewOrder", {ns: "textBoxFormModal"})
                        : t("modal.sendButton", {ns: "common"})
                }
                cancelText={t("modal.cancelButton2", {ns: "common"})}
                onOk={handleSubmit}
                data-testid={`${modalType}Modal`}
                okButtonProps={{
                    disabled:
                        isSubmitting ||
                        (modalType === "splitOrder"
                            ? checked.length === 0 || (!values.reason && !reasonText)
                            : false)
                }}
                onCancel={() => {
                    resetForm();
                    setVisible();
                    cancelFn();
                }}
                width={modalType === "splitOrder" ? 800 : undefined}
            >
                <Form autoComplete="off" labelCol={{span: 24}} wrapperCol={{span: 24}}>
                    {renderFormItem()}
                </Form>
            </Modal>
        </React.Fragment>
    ));
};
